import { useCallback, useEffect, useState } from "react";
import { useMutation } from "@apollo/client";

import { JobTable } from "./JobTable";
import { PageHeader } from "./PageHeader";

import { Text, VerticalSpacing } from "@otta/design";
import { Loading } from "@otta/shared-components";
import { handleMutationError } from "@toolbox/utils/error";
import {
  JobsListDocument,
  JobWorkflowStatus,
  UpdateJobWorkflowGroupBatchDocument,
} from "@toolbox/schema";
import { useQuery } from "@toolbox/apollo";

const ROW_LIMIT = 100;
export interface HandleToggleProps {
  ids: string[];
  workflowStatus: JobWorkflowStatus;
  disapprovalReason?: string;
}

export type JobStatuses = Map<
  string,
  { workflowStatus: JobWorkflowStatus; disapprovalReason: string | undefined }
>;

export const PaginatedTable = ({
  group,
  isCurrentPayingCustomer,
  companyName,
  scrapedLocation,
}: {
  group?: string;
  isCurrentPayingCustomer?: boolean;
  companyName?: string;
  scrapedLocation?: string;
}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [unsavedChanges, setUnsavedChanges] = useState<JobStatuses>(new Map());

  useEffect(() => {
    setUnsavedChanges(new Map());
  }, [group, isCurrentPayingCustomer, companyName, scrapedLocation]);

  const queryVariables = {
    limit: ROW_LIMIT,
    workflowStatus: JobWorkflowStatus.PreApproval,
    acquisitionChannel: group,
    isCurrentPayingCustomer: isCurrentPayingCustomer,
    companyName: companyName,
    scrapedLocation: scrapedLocation,
  };

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery(JobsListDocument, {
    variables: {
      ...queryVariables,
      offset: 0,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const refetchJobs = async (selectedPage: number) => {
    await refetch({
      ...queryVariables,
      offset: selectedPage * ROW_LIMIT,
    });
  };

  const jobs = data?.jobs ?? [];

  const [mutation, { loading: mutationLoading }] = useMutation(
    UpdateJobWorkflowGroupBatchDocument,
    {
      onError: handleMutationError,
    }
  );

  const updateJobs = async () => {
    const groups: {
      disapprovalReason: string | undefined;
      jobIds: string[];
      workflowStatus: JobWorkflowStatus;
    }[] = [];

    const entries = Array.from(unsavedChanges.entries());

    for (const [id, { workflowStatus, disapprovalReason }] of entries) {
      let group = groups.find(g => {
        return !disapprovalReason &&
          workflowStatus !== JobWorkflowStatus.Disapproved
          ? g.workflowStatus === workflowStatus
          : g.disapprovalReason === disapprovalReason;
      });

      if (!group) {
        group = {
          jobIds: [],
          workflowStatus,
          disapprovalReason,
        };
        groups.push(group);
      }
      group.jobIds.push(id);
    }

    await mutation({
      variables: {
        workflowGroups: groups,
      },
    });
  };

  const submitJobs = async () => {
    await Promise.all([updateJobs()]);
    await refetchJobs(currentPage);
    setUnsavedChanges(new Map());
  };

  const handlePageClick = ({ selected }: { selected: number }) => {
    setCurrentPage(selected);
    refetchJobs(selected);
  };

  const handleToggle = useCallback(
    ({ ids, workflowStatus, disapprovalReason }: HandleToggleProps) => {
      setUnsavedChanges(unsavedChanges => {
        const alreadySelected = ids.every(
          id =>
            unsavedChanges.get(id)?.workflowStatus === workflowStatus &&
            unsavedChanges.get(id)?.disapprovalReason === disapprovalReason
        );

        const newMap = new Map(unsavedChanges);

        ids.forEach(id => {
          if (alreadySelected) {
            newMap.delete(id);
          } else {
            newMap.set(id, {
              workflowStatus,
              disapprovalReason,
            });
          }
        });

        return newMap;
      });
    },
    []
  );

  const disableSubmit =
    queryLoading || mutationLoading || unsavedChanges.size === 0;

  if (queryLoading) {
    return <Loading />;
  }

  return (
    <VerticalSpacing>
      <PageHeader
        currentPage={currentPage}
        handlePageClick={handlePageClick}
        submitJobs={submitJobs}
        disableSubmit={disableSubmit}
        pageCount={data?.totalJobs ? Math.ceil(data?.totalJobs / ROW_LIMIT) : 0}
      />
      {jobs.length === 0 ? (
        <Text align="center">No jobs found!</Text>
      ) : (
        <JobTable
          jobs={jobs}
          unsavedChanges={unsavedChanges}
          handleToggle={handleToggle}
        />
      )}

      <PageHeader
        currentPage={currentPage}
        handlePageClick={handlePageClick}
        submitJobs={submitJobs}
        disableSubmit={
          queryLoading || mutationLoading || unsavedChanges.size === 0
        }
        pageCount={data?.totalJobs ? Math.ceil(data?.totalJobs / ROW_LIMIT) : 0}
      />
    </VerticalSpacing>
  );
};
